import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import {
  Business,
  Contact,
  CreateNotePayload,
  Household,
  LexicalText,
  Mention,
  Note,
  NoteAttachment,
  NoteTemplate,
  STARTING_NODES,
  Tag,
  UpdateNotePayload,
} from "@/schema"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { useNoteDialogStore } from "@/stores/use-note-dialog-store"
import { usePinnedDialogStore } from "@/stores/use-pinned-dialog-store"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import {
  BaseInlineInput,
  InlineBusinessSelect,
  InlineContactSelect,
  InlineHouseholdSelect,
  ReadonlyAttribute,
} from "@ui/forms/inline"
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { $createTextNode, $getRoot, $isElementNode, type LexicalNode } from "lexical"
import {
  AtSign,
  Building2,
  Check,
  Clock,
  Eye,
  EyeOff,
  History,
  Home,
  Lock,
  LockOpen,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  PictureInPicture2,
  Pin,
  Tag as TagIcon,
  Trash2,
  Upload,
  UserCircle2,
} from "lucide-react"
import { toast } from "sonner"

import { isAllBooksId } from "@/lib/all-books"
import { useFetchBusiness } from "@/hooks/api/businesses"
import { useRenderNoteTemplate } from "@/hooks/api/company-note-templates"
import { useFetchContact } from "@/hooks/api/contacts"
import { useFetchHousehold } from "@/hooks/api/households"
import { useCreateNote, useDeleteNote, useFetchNote, useUpdateNote } from "@/hooks/api/notes"
import { useFetchTags } from "@/hooks/api/tags"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { useEditorFocused } from "@/hooks/use-editor-focused"
import { useNoteTeamMentionables } from "@/hooks/use-note-team-mentionables"
import { ConfirmDialog } from "@/components/dialogs"
import { CustomDialogContent } from "@/components/dialogs/custom-dialog-content"
import { useDialogEntryId } from "@/components/dialogs/dialog-entry-context"
import { FloatingFormatBar } from "@/components/email/floating-format-bar"
import { Editor, type EditorRefHandle } from "@/components/lexical"
import { MentionNode } from "@/components/lexical/nodes/mention/node"
import { MentionDropdownPlugin, type MentionRecord } from "@/components/lexical/plugins/mention-dropdown"
import PasteImagePlugin from "@/components/lexical/plugins/paste-image"
import { VoiceMicButton } from "@/components/voice-transcription/voice-mic-button"

import { useDialogCloseGuard } from "../dialogs/use-dialog-close-guard"
import { MentionsHeader } from "./mentions-header"
import { addNoteAttachmentFiles } from "./note-attachment-files"
import { NoteAttachments, type DraftAttachment } from "./note-attachments"
import { NoteDialogSkeleton } from "./note-dialog-skeleton"
import { NoteTemplatePicker } from "./note-template-picker"
import { NoteVersionHistoryPanel } from "./note-version-history-panel"
import { ReferencedBusiness } from "./referenced-business"
import { ReferencedHousehold } from "./referenced-household"

const ACCOUNT_MENTION_TYPES = new Set(["CustodianAccount", "FinancialAccount", "InsurancePolicy"])

function isAccountMention(mention: Pick<Mention, "target_type">): boolean {
  return ACCOUNT_MENTION_TYPES.has(mention.target_type)
}

function stripAccountMentionsFromEditor(editor: ReturnType<EditorRefHandle["getEditor"]>) {
  if (!editor) return

  editor.update(() => {
    const root = $getRoot()
    const removeAccountMentions = (node: LexicalNode) => {
      if (node instanceof MentionNode && ACCOUNT_MENTION_TYPES.has(node.__recordType)) {
        const text = $createTextNode(node.__text)
        node.replace(text)
        return
      }
      if ($isElementNode(node)) {
        node.getChildren().forEach(removeAccountMentions)
      }
    }
    removeAccountMentions(root)
  })
}

function toDraftAttachments(attachments: NoteAttachment[] = []): DraftAttachment[] {
  return attachments.map((attachment) =>
    attachment.source === "uploaded"
      ? {
          kind: "existing-file",
          signedId: attachment.signed_id,
          fileName: attachment.file_name,
          downloadUrl: attachment.download_url,
        }
      : { kind: "link", url: attachment.url, name: attachment.name }
  )
}

function editorContentForSnapshot(content: LexicalText): LexicalText {
  return content.nodes || content.html || content.markdown ? content : { ...content, nodes: STARTING_NODES }
}

interface NoteDialogProps {
  noteId: string
  dialogId: string
  householdId?: string
  contactId?: string
  businessId?: string
  initialContent?: LexicalText
  initialMentions?: Mention[]
  onSaved?: () => void
}

export function NoteDialog({
  noteId,
  dialogId,
  householdId,
  contactId,
  businessId,
  initialContent,
  initialMentions,
  onSaved,
}: NoteDialogProps) {
  const dialogStore = useGlobalDialogStore()
  const entryId = useDialogEntryId()
  const setDialogContentClasses = useGlobalDialogStore((state) => state.setDialogContentClasses)
  const {
    pinnedDialogs,
    closeDialog: closePinnedDialog,
    fullScreenDialogId,
    toggleFullScreen,
    unpinDialog,
  } = usePinnedDialogStore()
  const initializeDraft = useNoteDialogStore((state) => state.initializeDraft)
  const updateDraft = useNoteDialogStore((state) => state.updateDraft)
  const removeDraft = useNoteDialogStore((state) => state.removeDraft)
  const dialogIdRef = useRef(dialogId)
  const stableDialogId = dialogIdRef.current
  const draft = useNoteDialogStore((state) => state.drafts[stableDialogId])
  const bookId = useBookId()
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  const [isViewingHistory, setIsViewingHistory] = useState(false)
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showHideDialog, setShowHideDialog] = useState(false)

  const initialContentRef = useRef(initialContent)
  useEffect(() => {
    if (initialContent && initialContentRef.current !== initialContent) {
      initialContentRef.current = initialContent
    }
  }, [initialContent])

  useEffect(() => {
    if (!initialContentRef.current || !draft) {
      return
    }

    if (draft.note?.content) {
      return
    }

    updateDraft(stableDialogId, (current) => {
      if (current.note?.content) {
        return current
      }

      return {
        ...current,
        note: {
          ...current.note,
          content: initialContentRef.current ?? current.note?.content,
        },
      }
    })
  }, [draft, stableDialogId, updateDraft])

  const [isEditorReady, setIsEditorReady] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedHousehold, setSelectedHousehold] = useState<Household | null>(null)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null)
  const [mentions, setMentions] = useState<Mention[]>(initialMentions ?? [])
  const [selectedTags, setSelectedTags] = useState<Tag[]>([])

  const isPinned = pinnedDialogs.some((dialog) => dialog.id === stableDialogId)
  const isFullScreen = fullScreenDialogId === stableDialogId

  useLayoutEffect(() => {
    if (isPinned || !entryId) return

    setDialogContentClasses(
      `h-[min(48rem,calc(100vh-8rem))] max-h-[calc(100vh-8rem)] ${
        isVersionHistoryOpen ? "max-w-5xl" : "max-w-2xl"
      } overflow-hidden transition-[max-width] duration-200`,
      entryId
    )
  }, [isPinned, isVersionHistoryOpen, setDialogContentClasses, entryId])

  const initialParamsRef = useRef({
    noteId: noteId ?? "",
    householdId: householdId ?? null,
    contactId: contactId ?? null,
    businessId: businessId ?? null,
  })

  const note = (draft?.note ?? {}) as Note
  const isLocked = !!note.locked_at
  const selectedHouseholdId = draft?.selectedHouseholdId ?? null
  const selectedContactId = draft?.selectedContactId ?? null
  const selectedBusinessId = draft?.selectedBusinessId ?? null
  const selectedNotableBookId = selectedContactId
    ? selectedContact?.book_id
    : selectedHouseholdId
      ? selectedHousehold?.book_id
      : selectedBusiness?.book_id
  const noteBookId = note.book_id ?? selectedNotableBookId ?? (isAllBooksId(bookId) ? undefined : bookId)
  const teamOptions = useNoteTeamMentionables(noteBookId)
  // Records store the raw provider folder id they're linked to; the drive attachment
  // picker opens there so users land on the record's files instead of the drive root.
  const notableStorageFolderId =
    (selectedContactId
      ? selectedContact?.storage_folder_id
      : selectedHouseholdId
        ? selectedHousehold?.storage_folder_id
        : selectedBusiness?.storage_folder_id) ?? undefined
  const notableStorageFolderName =
    (selectedContactId
      ? selectedContact?.person?.full_name
      : selectedHouseholdId
        ? selectedHousehold?.name
        : selectedBusiness?.name) ?? undefined
  const attachments = useMemo(() => draft?.attachments ?? [], [draft?.attachments])
  const editorInitialContent = note.content ?? initialContentRef.current
  const isDirty = draft?.isDirty ?? false
  const storedNoteId = draft?.noteId ?? noteId ?? ""
  const isExistingNote = !!(storedNoteId || note.id)

  const { mutate: createNoteMutate } = useCreateNote()
  const { mutate: updateNoteMutate, isPending: isUpdatingNote } = useUpdateNote()
  const deleteNoteMutation = useDeleteNote()
  const { data: currentUser } = useFetchCurrentUser()
  const isAdmin = currentUser?.capabilities.includes("manage_note_locks") ?? false
  const { data: tagsData } = useFetchTags("note")
  const availableTags = useMemo(() => {
    if (!tagsData?.tags) return []
    return [...tagsData.tags].sort((a, b) => a.name.localeCompare(b.name))
  }, [tagsData?.tags])

  const handleTagToggle = (tag: Tag) => {
    setSelectedTags((prev) => {
      const isSelected = prev.some((t) => t.id === tag.id)
      return isSelected ? prev.filter((t) => t.id !== tag.id) : [...prev, tag]
    })
    updateDraft(stableDialogId, (current) => ({ ...current, isDirty: true }))
  }
  const { mutateAsync: renderTemplate } = useRenderNoteTemplate()
  const { data: existingNote } = useFetchNote(storedNoteId || "")
  const householdQuery = useFetchHousehold(selectedHouseholdId || "", { enabled: !!selectedHouseholdId })
  const contactQuery = useFetchContact(selectedContactId)
  const businessQuery = useFetchBusiness(selectedBusinessId)
  const editorRef = useRef<EditorRefHandle | null>(null)
  const isEditorFocused = useEditorFocused(editorRef)
  const isPreviewingVersionRef = useRef(false)
  const hasAutoFocusedRef = useRef(false)

  useEffect(() => {
    initializeDraft(stableDialogId, {
      noteId: initialParamsRef.current.noteId,
      selectedHouseholdId: initialParamsRef.current.householdId,
      selectedContactId: initialParamsRef.current.contactId,
      selectedBusinessId: initialParamsRef.current.businessId,
    })
  }, [initializeDraft, stableDialogId])

  useEffect(() => {
    if (!noteId) {
      setIsEditorReady(true)
    } else if (existingNote) {
      setIsEditorReady(true)
    }
  }, [existingNote, noteId])

  useEffect(() => {
    if (!existingNote || !draft) return

    const nextNoteId = existingNote.id ?? ""
    const nextUpdatedAt = existingNote.updated_at ?? null
    const currentNoteId = draft.note?.id ?? ""
    const currentUpdatedAt = draft.note?.updated_at ?? null

    const isHouseholdNote = existingNote.notable_type === "Household" && existingNote.notable_id
    const isContactNote = existingNote.notable_type === "Contact" && existingNote.notable_id
    const isBusinessNote = existingNote.notable_type === "Business" && existingNote.notable_id
    const nextHouseholdId = isHouseholdNote ? (existingNote.notable_id ?? null) : null
    const nextContactId = isContactNote ? (existingNote.notable_id ?? null) : null
    const nextBusinessId = isBusinessNote ? (existingNote.notable_id ?? null) : null

    const noteChanged = currentNoteId !== nextNoteId || currentUpdatedAt !== nextUpdatedAt
    const needsHouseholdUpdate = (draft.selectedHouseholdId ?? null) !== nextHouseholdId
    const needsContactUpdate = (draft.selectedContactId ?? null) !== nextContactId
    const needsBusinessUpdate = (draft.selectedBusinessId ?? null) !== nextBusinessId

    if (!noteChanged && !needsHouseholdUpdate && !needsContactUpdate && !needsBusinessUpdate) {
      return
    }

    updateDraft(stableDialogId, (current) => {
      const currentId = current.note?.id ?? ""
      const currentUpdated = current.note?.updated_at ?? null
      const currentNoteChanged = currentId !== nextNoteId || currentUpdated !== nextUpdatedAt
      const currentNeedsHousehold = (current.selectedHouseholdId ?? null) !== nextHouseholdId
      const currentNeedsContact = (current.selectedContactId ?? null) !== nextContactId
      const currentNeedsBusiness = (current.selectedBusinessId ?? null) !== nextBusinessId

      if (
        !currentNoteChanged &&
        !currentNeedsHousehold &&
        !currentNeedsContact &&
        !currentNeedsBusiness &&
        !current.isDirty
      ) {
        return current
      }

      return {
        ...current,
        note: currentNoteChanged ? existingNote : current.note,
        noteId: nextNoteId,
        isDirty: currentNoteChanged ? false : current.isDirty,
        attachments: currentNoteChanged ? toDraftAttachments(existingNote.attachments) : current.attachments,
        selectedHouseholdId: currentNeedsHousehold ? nextHouseholdId : current.selectedHouseholdId,
        selectedContactId: currentNeedsContact ? nextContactId : current.selectedContactId,
        selectedBusinessId: currentNeedsBusiness ? nextBusinessId : current.selectedBusinessId,
      }
    })
  }, [draft, existingNote, stableDialogId, updateDraft])

  useEffect(() => {
    if (selectedHouseholdId && householdQuery.data) {
      setSelectedHousehold(householdQuery.data)
    } else if (!selectedHouseholdId) {
      setSelectedHousehold(null)
    }
  }, [householdQuery.data, selectedHouseholdId])

  useEffect(() => {
    if (selectedContactId && contactQuery.data) {
      setSelectedContact(contactQuery.data)
    } else if (!selectedContactId) {
      setSelectedContact(null)
    }
  }, [contactQuery.data, selectedContactId])

  useEffect(() => {
    if (selectedBusinessId && businessQuery.data) {
      setSelectedBusiness(businessQuery.data)
    } else if (!selectedBusinessId) {
      setSelectedBusiness(null)
    }
  }, [businessQuery.data, selectedBusinessId])

  const hasAppliedInitialMentionsRef = useRef(false)

  useEffect(() => {
    if (note.mentions !== undefined) {
      setMentions(note.mentions)
      return
    }

    if (!note.id && initialMentions && initialMentions.length > 0 && !hasAppliedInitialMentionsRef.current) {
      setMentions(initialMentions)
      hasAppliedInitialMentionsRef.current = true
    }
  }, [note.mentions, note.id, initialMentions])

  useEffect(() => {
    setSelectedTags(note.tags?.length ? note.tags : [])
  }, [note.tags])

  useEffect(() => {
    if (!isEditorReady || hasAutoFocusedRef.current) return
    hasAutoFocusedRef.current = true
    const timer = window.setTimeout(() => {
      editorRef.current?.focus(isExistingNote ? { defaultSelection: "rootStart" } : undefined)
    }, 100)

    return () => window.clearTimeout(timer)
  }, [isEditorReady, isExistingNote])

  const handleMentionsChange = useCallback(
    (newMentions: Mention[]) => {
      setMentions(newMentions)
      updateDraft(stableDialogId, (current) => ({
        ...current,
        isDirty: true,
      }))
    },
    [stableDialogId, updateDraft]
  )

  const handleDropdownMentionAdded = useCallback((mention: MentionRecord) => {
    setMentions((current) => {
      const key = `${mention.recordType}-${mention.id}`
      const exists = current.some((m) => `${m.target_type}-${m.target_id}` === key)
      if (exists) return current

      // Only add mentions that match valid MentionType
      const validTypes: MentionRecord["recordType"][] = [
        "Client",
        "Prospect",
        "Person",
        "Business",
        "Contact",
        "Household",
        "User",
        "Team",
        "Trust",
        "CustodianAccount",
        "FinancialAccount",
        "InsurancePolicy",
      ]
      if (!validTypes.includes(mention.recordType)) {
        return current
      }

      return [
        ...current,
        {
          target_id: mention.id,
          target_label: mention.label,
          target_type: mention.recordType as Mention["target_type"],
        },
      ]
    })
  }, [])

  const handleDropdownMentionRemoved = useCallback((mention: MentionRecord) => {
    setMentions((current) => {
      const key = `${mention.recordType}-${mention.id}`
      return current.filter((m) => `${m.target_type}-${m.target_id}` !== key)
    })
  }, [])

  const handleContentChange = useCallback(
    (lexicalText: LexicalText) => {
      if (!draft || isPreviewingVersionRef.current) return
      updateDraft(stableDialogId, (current) => ({
        ...current,
        note: {
          ...current.note,
          content: lexicalText,
        },
        isDirty: true,
      }))
    },
    [draft, stableDialogId, updateDraft]
  )

  const handleHtmlConverted = useCallback(
    (lexicalText: LexicalText) => {
      if (!draft) return
      // The editor auto-converts a legacy note's HTML to nodes on open; that is not a user edit,
      // so it must not mark the draft dirty (real edits flow through handleContentChange). Marking
      // it here would pop "Discard unsaved note?" the moment an untouched legacy note is closed.
      updateDraft(stableDialogId, (current) => ({
        ...current,
        note: {
          ...current.note,
          content: lexicalText,
        },
      }))
    },
    [draft, stableDialogId, updateDraft]
  )

  const handleAttachmentsChange = useCallback(
    (next: DraftAttachment[]) => {
      updateDraft(stableDialogId, (current) => ({ ...current, attachments: next, isDirty: true }))
    },
    [stableDialogId, updateDraft]
  )

  const handleFileDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!Array.from(event.dataTransfer.types).includes("Files")) return
      event.preventDefault()
      if (isLocked || isVersionHistoryOpen) return

      event.dataTransfer.dropEffect = "copy"
      setIsDraggingFiles(true)
    },
    [isLocked, isVersionHistoryOpen]
  )

  const handleFileDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
    setIsDraggingFiles(false)
  }, [])

  const handleFileDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!Array.from(event.dataTransfer.types).includes("Files")) return
      event.preventDefault()
      setIsDraggingFiles(false)
      if (isLocked || isVersionHistoryOpen) return

      const files = Array.from(event.dataTransfer.files)
      if (files.length === 0) return

      const result = addNoteAttachmentFiles(attachments, files)
      if (!result.ok) {
        toast.error(result.error)
        return
      }

      handleAttachmentsChange(result.attachments)
    },
    [attachments, handleAttachmentsChange, isLocked, isVersionHistoryOpen]
  )

  const handleHouseholdSelection = useCallback(
    (id: string | null) => {
      updateDraft(stableDialogId, (current) => ({
        ...current,
        selectedHouseholdId: id,
        selectedContactId: id ? null : current.selectedContactId,
        selectedBusinessId: id ? null : current.selectedBusinessId,
        isDirty: true,
      }))
      setSelectedHousehold(null)
      // Account mentions are household-scoped; drop them when the household changes
      // so a save cannot persist another household's accounts (or fail after insert).
      setMentions((current) => current.filter((mention) => !isAccountMention(mention)))
      stripAccountMentionsFromEditor(editorRef.current?.getEditor() ?? null)
      if (id) {
        setSelectedContact(null)
        setSelectedBusiness(null)
      }
    },
    [stableDialogId, updateDraft]
  )

  const handleContactSelection = useCallback(
    (id: string | null) => {
      updateDraft(stableDialogId, (current) => ({
        ...current,
        selectedContactId: id,
        selectedHouseholdId: id ? null : current.selectedHouseholdId,
        selectedBusinessId: id ? null : current.selectedBusinessId,
        isDirty: true,
      }))
      setSelectedContact(null)
      if (id) {
        setSelectedHousehold(null)
        setMentions((current) => current.filter((mention) => !isAccountMention(mention)))
        stripAccountMentionsFromEditor(editorRef.current?.getEditor() ?? null)
        setSelectedBusiness(null)
      }
    },
    [stableDialogId, updateDraft]
  )

  const handleBusinessSelection = useCallback(
    (id: string | null) => {
      updateDraft(stableDialogId, (current) => ({
        ...current,
        selectedBusinessId: id,
        selectedHouseholdId: id ? null : current.selectedHouseholdId,
        selectedContactId: id ? null : current.selectedContactId,
        isDirty: true,
      }))
      setSelectedBusiness(null)
      if (id) {
        setSelectedHousehold(null)
        setSelectedContact(null)
        setMentions((current) => current.filter((mention) => !isAccountMention(mention)))
        stripAccountMentionsFromEditor(editorRef.current?.getEditor() ?? null)
      }
    },
    [stableDialogId, updateDraft]
  )

  const handleCancel = useCallback(() => {
    // Dispose on explicit close, not unmount: pinning and Strict Mode remount the composer.
    removeDraft(stableDialogId)
    if (isPinned) {
      closePinnedDialog(stableDialogId)
    } else {
      dialogStore.closeDialog()
    }
  }, [closePinnedDialog, dialogStore, isPinned, removeDraft, stableDialogId])

  const { requestClose, discardConfirm } = useDialogCloseGuard({
    isDirty,
    entityLabel: "note",
    onClose: handleCancel,
    isPinned,
    dialogId: stableDialogId,
  })

  const handleSave = useCallback(() => {
    if (!draft || isSaving) return

    const noteData = draft.note || {}
    const notableId = selectedContactId ?? selectedHouseholdId ?? selectedBusinessId ?? null
    const noteContent = noteData.content ?? initialContentRef.current
    const savedCallback = onSaved ?? draft?.onSaved

    if (!notableId) {
      toast.error("Select a household, contact, or business before saving")
      return
    }

    setIsSaving(true)

    const keptSignedIds: string[] = []
    const newFiles: File[] = []
    const links: { url: string; name: string }[] = []
    for (const attachment of attachments) {
      if (attachment.kind === "existing-file") keptSignedIds.push(attachment.signedId)
      else if (attachment.kind === "new-file") newFiles.push(attachment.file)
      else links.push({ url: attachment.url, name: attachment.name })
    }

    if (!noteData.id) {
      const payload: CreateNotePayload = {
        content: noteContent,
        notable_type: selectedContactId ? "Contact" : selectedHouseholdId ? "Household" : "Business",
        notable_id: notableId,
        pinned: noteData.pinned ?? false,
        mentions: mentions,
        tag_ids: selectedTags.map((t) => t.id),
        files: keptSignedIds,
        links,
      }

      // In the All Books view the URL carries no concrete book, so send the notable's
      // owning book as the override so the note lands in the right book/company.
      createNoteMutate(
        { ...payload, newFiles, overrideBookId: selectedNotableBookId },
        {
          onSuccess: () => {
            savedCallback?.()
            removeDraft(stableDialogId)
            setIsSaving(false)
            toast.success("Note saved")
            if (isPinned) {
              closePinnedDialog(stableDialogId)
            } else {
              dialogStore.closeDialog()
            }
          },
          onError: () => {
            setIsSaving(false)
          },
        }
      )
    } else {
      const payload: UpdateNotePayload = {
        id: noteData.id,
        content: noteContent || {},
        pinned: noteData.pinned,
        mentions: mentions,
        tag_ids: selectedTags.map((t) => t.id),
        files: keptSignedIds,
        links,
      }

      updateNoteMutate(
        { ...payload, newFiles },
        {
          onSuccess: (data: Note) => {
            savedCallback?.()
            updateDraft(stableDialogId, (current) => ({
              ...current,
              note: data,
              noteId: data.id ?? current.noteId,
              isDirty: false,
            }))
            removeDraft(stableDialogId)
            setIsSaving(false)
            toast.success("Note saved")
            if (isPinned) {
              closePinnedDialog(stableDialogId)
            } else {
              dialogStore.closeDialog()
            }
          },
          onError: () => {
            setIsSaving(false)
          },
        }
      )
    }
  }, [
    closePinnedDialog,
    createNoteMutate,
    dialogStore,
    draft,
    isPinned,
    isSaving,
    mentions,
    selectedTags,
    attachments,
    selectedContactId,
    selectedHouseholdId,
    selectedBusinessId,
    selectedNotableBookId,
    stableDialogId,
    updateDraft,
    updateNoteMutate,
    removeDraft,
    onSaved,
  ])

  const handlePinToScreen = useCallback(() => {
    if (isPinned) {
      unpinDialog(stableDialogId)
    } else {
      dialogStore.pinDialog(stableDialogId, "note", bookId)
    }
  }, [bookId, dialogStore, isPinned, stableDialogId, unpinDialog])

  const handleToggleNotePinned = useCallback(() => {
    updateDraft(stableDialogId, (current) => ({
      ...current,
      note: {
        ...current.note,
        pinned: !current.note?.pinned,
      },
      isDirty: true,
    }))
  }, [stableDialogId, updateDraft])

  const handleToggleHidden = useCallback(() => {
    if (!note.id || isUpdatingNote) return

    const hidden = !note.hidden
    updateNoteMutate(
      { id: note.id, hidden },
      {
        onSuccess: (updatedNote) => {
          updateDraft(stableDialogId, (current) => ({
            ...current,
            note: {
              ...current.note,
              hidden: updatedNote.hidden,
              updated_at: updatedNote.updated_at,
              updated_by_id: updatedNote.updated_by_id,
            },
          }))
          setShowHideDialog(false)
          toast.success(hidden ? "Note hidden" : "Note unhidden")
        },
      }
    )
  }, [isUpdatingNote, note.hidden, note.id, stableDialogId, updateDraft, updateNoteMutate])

  const handleToggleLocked = useCallback(() => {
    if (!note.id || isUpdatingNote || isDirty) return

    const locked = !isLocked
    updateNoteMutate(
      { id: note.id, locked },
      {
        onSuccess: (updatedNote) => {
          updateDraft(stableDialogId, (current) => ({
            ...current,
            note: {
              ...current.note,
              locked_at: updatedNote.locked_at,
              updated_at: updatedNote.updated_at,
              updated_by_id: updatedNote.updated_by_id,
            },
          }))
          toast.success(locked ? "Note locked" : "Note unlocked")
        },
      }
    )
  }, [isDirty, isLocked, isUpdatingNote, note.id, stableDialogId, updateDraft, updateNoteMutate])

  const handleDeleteNote = useCallback(() => {
    if (!note.id) return

    deleteNoteMutation.mutate(
      { id: note.id },
      {
        onSuccess: () => {
          setShowDeleteDialog(false)
          removeDraft(stableDialogId)
          toast.success("Note deleted")
          if (isPinned) {
            closePinnedDialog(stableDialogId)
          } else {
            dialogStore.closeDialog()
          }
        },
      }
    )
  }, [closePinnedDialog, deleteNoteMutation, dialogStore, isPinned, note.id, removeDraft, stableDialogId])

  const handlePreviewVersion = useCallback((content: LexicalText, isHistorical: boolean) => {
    isPreviewingVersionRef.current = true
    setIsViewingHistory(isHistorical)
    editorRef.current?.updateContent(editorContentForSnapshot(content))
    if (!isHistorical) queueMicrotask(() => (isPreviewingVersionRef.current = false))
  }, [])

  const handleOpenVersionHistory = useCallback(() => {
    if (isDirty) {
      toast.info("Save or discard your changes before viewing version history")
      return
    }

    setIsVersionHistoryOpen(true)
  }, [isDirty])

  const handleVersionRestored = useCallback(
    (restoredNote: Note) => {
      updateDraft(stableDialogId, (current) => ({
        ...current,
        note: restoredNote,
        isDirty: false,
      }))
      isPreviewingVersionRef.current = true
      if (restoredNote.content) editorRef.current?.updateContent(editorContentForSnapshot(restoredNote.content))
      queueMicrotask(() => (isPreviewingVersionRef.current = false))
      setIsViewingHistory(false)
    },
    [stableDialogId, updateDraft]
  )

  const handleApplyTemplate = useCallback(
    async (template: NoteTemplate) => {
      if (template.attachments?.length) {
        const templateAttachments = toDraftAttachments(template.attachments)
        updateDraft(stableDialogId, (current) => ({
          ...current,
          attachments: [...current.attachments, ...templateAttachments],
          isDirty: true,
        }))
      }

      if (!template.content) return

      const applyContent = (content: LexicalText) => {
        editorRef.current?.updateContent(content)
        updateDraft(stableDialogId, (current) => ({
          ...current,
          note: {
            ...current.note,
            content,
          },
          isDirty: true,
        }))
      }

      const currentHouseholdId = selectedHouseholdId ?? householdId
      renderTemplate(
        { noteTemplateId: template.id, householdId: currentHouseholdId ?? undefined },
        {
          onSuccess: (data) => {
            if (data.note_template_render.content) {
              applyContent(data.note_template_render.content)
            } else {
              applyContent(template.content!)
            }
          },
          onError: () => {
            applyContent(template.content!)
          },
        }
      )
    },
    [stableDialogId, updateDraft, renderTemplate, selectedHouseholdId, householdId]
  )

  const hasHouseholdProp = !!householdId
  const hasContactProp = !!contactId
  const hasBusinessProp = !!businessId
  const isHouseholdReadOnly = hasHouseholdProp || (isExistingNote && !!selectedHousehold)
  const isContactReadOnly = hasContactProp || (isExistingNote && !!selectedContact)
  const isBusinessReadOnly = hasBusinessProp || (isExistingNote && !!selectedBusiness)
  const showHouseholdField = isExistingNote
    ? note.notable_type === "Household"
    : hasHouseholdProp || (!hasContactProp && !hasBusinessProp)
  const showContactField = isExistingNote
    ? note.notable_type === "Contact"
    : hasContactProp || (!hasHouseholdProp && !hasBusinessProp)
  const showBusinessField = isExistingNote
    ? note.notable_type === "Business"
    : hasBusinessProp || (!hasHouseholdProp && !hasContactProp)

  const isReferencedNote =
    (note.notable_type === "Household" &&
      note.notable_id &&
      ((householdId && note.notable_id !== householdId) || contactId)) ||
    (note.notable_type === "Contact" &&
      note.notable_id &&
      ((contactId && note.notable_id !== contactId) || householdId)) ||
    (note.notable_type === "Business" &&
      note.notable_id &&
      ((businessId && note.notable_id !== businessId) || householdId || contactId))

  const createdAtDate = note.created_at ? new Date(note.created_at) : null
  const updatedAtDate = note.updated_at ? new Date(note.updated_at) : null
  const formattedCreatedAt = createdAtDate ? format(createdAtDate, "MMM d, yyyy h:mm a") : undefined
  const formattedUpdatedAt =
    updatedAtDate && createdAtDate && Math.abs(updatedAtDate.getTime() - createdAtDate.getTime()) > 1000
      ? format(updatedAtDate, "MMM d, yyyy h:mm a")
      : undefined

  const editorPlaceholder = "Press '@' for mentions or '/' for commands"
  const selectedNotableIsReady = !isAllBooksId(bookId) || !!note.id || !!selectedNotableBookId
  const canSave =
    !!draft &&
    !isSaving &&
    selectedNotableIsReady &&
    (selectedHouseholdId !== null ||
      selectedContactId !== null ||
      selectedBusinessId !== null ||
      hasHouseholdProp ||
      hasContactProp ||
      hasBusinessProp) &&
    (isDirty || !note.id)

  const saveButtonLabel = note.id ? "Update note" : "Create note"

  const isNotePinned = !!note.pinned

  const headerActions = useMemo(() => {
    return [
      ...(isPinned
        ? [
            {
              key: "full-screen",
              label: isFullScreen ? "Restore dialog" : "Open full screen",
              icon: isFullScreen ? Minimize2 : Maximize2,
              onClick: () => toggleFullScreen(stableDialogId),
              active: isFullScreen,
            },
          ]
        : []),
      {
        key: "pin-to-screen",
        label: isPinned ? "Unpin" : "Pin to screen",
        icon: PictureInPicture2,
        onClick: handlePinToScreen,
        active: isPinned,
      },
      ...(note.id
        ? [
            {
              key: "note-actions",
              content: (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="iconXs" aria-label="Note actions">
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={handleOpenVersionHistory}>
                      <History />
                      Version history
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setShowHideDialog(true)} disabled={isLocked || isUpdatingNote}>
                      {note.hidden ? <Eye /> : <EyeOff />}
                      {note.hidden ? "Unhide" : "Hide"}
                    </DropdownMenuItem>
                    {isAdmin ? (
                      <DropdownMenuItem onSelect={handleToggleLocked} disabled={isDirty || isUpdatingNote}>
                        {isLocked ? <LockOpen /> : <Lock />}
                        {isLocked ? "Unlock" : "Lock"}
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      onSelect={() => setShowDeleteDialog(true)}
                      disabled={isLocked || deleteNoteMutation.isPending}
                      className="text-destructive"
                    >
                      <Trash2 />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]
        : []),
    ]
  }, [
    deleteNoteMutation.isPending,
    handleOpenVersionHistory,
    handlePinToScreen,
    handleToggleLocked,
    isAdmin,
    isDirty,
    isFullScreen,
    isLocked,
    isPinned,
    isUpdatingNote,
    note.hidden,
    note.id,
    stableDialogId,
    toggleFullScreen,
  ])

  if (!isEditorReady) {
    return <NoteDialogSkeleton />
  }

  return (
    <CustomDialogContent title="Note" titleProvided>
      <div
        className="relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
        onDragOver={handleFileDragOver}
        onDragLeave={handleFileDragLeave}
        onDrop={handleFileDrop}
      >
        {isDraggingFiles && (
          <div className="border-primary bg-background/90 pointer-events-none absolute inset-2 z-50 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed backdrop-blur-sm">
            <Upload className="text-primary size-8" />
            <span className="text-sm font-semibold">Drop files to attach</span>
          </div>
        )}
        <DialogHeader actions={headerActions} className="min-w-0 flex-none justify-end gap-2 p-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="iconXs"
                className="-ml-1.5"
                onClick={handleToggleNotePinned}
                disabled={isLocked}
              >
                <Pin className={cn("text-icon", isNotePinned && "fill-icon")} />
                <span className="sr-only">{isNotePinned ? "Unpin note" : "Pin note"}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isNotePinned ? "Unpin note" : "Pin note"}</TooltipContent>
          </Tooltip>
          <VisuallyHidden>
            <DialogTitle>{noteId ? "Edit Note" : "New Note"}</DialogTitle>
          </VisuallyHidden>
        </DialogHeader>
        <div className="flex min-h-0 min-w-0 flex-1">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
              <div className="mx-4 min-w-0 gap-4 space-y-1 border-b p-4">
                {showHouseholdField &&
                  (isHouseholdReadOnly ? (
                    isReferencedNote && note.notable_id ? (
                      <ReferencedHousehold householdId={note.notable_id} householdName={selectedHousehold?.name} />
                    ) : (
                      <ReadonlyAttribute
                        label="Household"
                        LabelIcon={Home}
                        value={selectedHousehold?.name}
                        placeholder="Loading household..."
                        fixedLabelWidth="120px"
                      />
                    )
                  ) : (
                    <InlineHouseholdSelect
                      label="Household"
                      LabelIcon={Home}
                      selectedHousehold={selectedHousehold}
                      setSelectedHouseholdId={handleHouseholdSelection}
                      isDisabled={isExistingNote || !!selectedContactId}
                      fixedLabelWidth="120px"
                    />
                  ))}
                {showContactField &&
                  (isContactReadOnly ? (
                    <ReadonlyAttribute
                      label="Contact"
                      LabelIcon={UserCircle2}
                      value={selectedContact?.person?.full_name ?? ""}
                      placeholder="Loading contact..."
                      fixedLabelWidth="120px"
                    />
                  ) : (
                    <InlineContactSelect
                      label="Contact"
                      LabelIcon={UserCircle2}
                      selectedContact={selectedContact}
                      setSelectedContactId={handleContactSelection}
                      isDisabled={isExistingNote || !!selectedHouseholdId}
                      fixedLabelWidth="120px"
                    />
                  ))}
                {showBusinessField &&
                  (isBusinessReadOnly ? (
                    isReferencedNote && note.notable_id ? (
                      <ReferencedBusiness businessId={note.notable_id} businessName={selectedBusiness?.name} />
                    ) : (
                      <ReadonlyAttribute
                        label="Business"
                        LabelIcon={Building2}
                        value={selectedBusiness?.name ?? ""}
                        placeholder="Loading business..."
                        fixedLabelWidth="120px"
                      />
                    )
                  ) : (
                    <InlineBusinessSelect
                      label="Business"
                      LabelIcon={Building2}
                      selectedBusiness={selectedBusiness}
                      setSelectedBusinessId={handleBusinessSelection}
                      isDisabled={isExistingNote || !!selectedHouseholdId || !!selectedContactId}
                      fixedLabelWidth="120px"
                    />
                  ))}
                <MentionsHeader
                  mentions={mentions}
                  bookId={noteBookId}
                  teamOptions={teamOptions}
                  onMentionsChange={handleMentionsChange}
                  householdId={selectedHouseholdId}
                  disabled={isVersionHistoryOpen}
                  fixedLabelWidth="120px"
                  LabelIcon={AtSign}
                />
                <BaseInlineInput label="Labels" LabelIcon={TagIcon} fixedLabelWidth="120px">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        disabled={isVersionHistoryOpen}
                        className={cn(
                          "hover:bg-muted focus:outline-primary flex min-h-8 w-full cursor-pointer items-center gap-1.5 rounded-md border border-transparent px-2 text-left text-sm focus:outline-1",
                          !selectedTags.length && "text-muted-foreground"
                        )}
                      >
                        {selectedTags.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-1">
                            {selectedTags.map((tag) => (
                              <Badge key={tag.id} variant="outline" size="base">
                                <div
                                  className="mr-1 size-1.5 flex-none rounded-full"
                                  style={{ backgroundColor: tag.color || "var(--color-secondary)" }}
                                />
                                {tag.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span>None</span>
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-56 p-0">
                      <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
                        <CommandInput placeholder="Search labels..." />
                        <CommandList>
                          <CommandEmpty>No labels found</CommandEmpty>
                          <CommandGroup>
                            {availableTags.map((tag) => (
                              <CommandItem key={tag.id} value={tag.name} onSelect={() => handleTagToggle(tag)}>
                                <div
                                  className="size-2 flex-none rounded-full"
                                  style={{ backgroundColor: tag.color || "var(--color-secondary)" }}
                                />
                                <span className="flex-1 truncate text-left text-sm">{tag.name}</span>
                                {selectedTags.some((t) => t.id === tag.id) && (
                                  <Check className="text-foreground size-4 shrink-0" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </BaseInlineInput>
                <NoteAttachments
                  attachments={attachments}
                  onChange={handleAttachmentsChange}
                  disabled={isLocked || isVersionHistoryOpen}
                  storageFolderId={notableStorageFolderId}
                  storageFolderName={notableStorageFolderName}
                />
                {formattedCreatedAt ? (
                  <ReadonlyAttribute
                    label="Created"
                    value={formattedCreatedAt}
                    fixedLabelWidth="120px"
                    LabelIcon={Clock}
                  />
                ) : null}
                {formattedUpdatedAt ? (
                  <ReadonlyAttribute
                    label="Last changed"
                    value={formattedUpdatedAt}
                    fixedLabelWidth="120px"
                    LabelIcon={Clock}
                  />
                ) : null}
              </div>
              <div className="min-w-0 cursor-default p-4 px-6 pb-14">
                <Editor
                  ref={editorRef}
                  onChange={handleContentChange}
                  onHtmlConverted={handleHtmlConverted}
                  editable={!isLocked && !isViewingHistory && !isVersionHistoryOpen}
                  placeholder={editorPlaceholder}
                  startingPlaceholder={editorPlaceholder}
                  initialContent={editorInitialContent}
                  styleOverrides={{
                    "--lexical-editor-min-height": "240px",
                  }}
                >
                  <PasteImagePlugin />
                  <MentionDropdownPlugin
                    bookId={noteBookId}
                    teamOptions={teamOptions}
                    mentionableTypes={["client", "prospect", "person", "business", "contact", "trust", "user"]}
                    scopeUsersToBook
                    householdId={selectedHouseholdId}
                    onMentionAdded={handleDropdownMentionAdded}
                    onMentionRemoved={handleDropdownMentionRemoved}
                  />
                </Editor>
              </div>
            </div>
            <div className="relative shrink-0">
              <FloatingFormatBar
                enableChecklist
                editorRef={editorRef}
                visible={isEditorFocused && !isLocked && !isVersionHistoryOpen && !isViewingHistory}
              />
              <DialogFooter className="min-w-0 flex-none justify-end gap-2 p-3">
                <div className="mr-auto flex items-center gap-1">
                  {isLocked ? (
                    <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                      <Lock className="size-3.5" />
                      Locked — view only
                    </div>
                  ) : isVersionHistoryOpen ? (
                    <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                      <History className="size-3.5" />
                      Viewing version history
                    </div>
                  ) : (
                    <>
                      <VoiceMicButton editorRef={editorRef} />
                      {!isExistingNote && <NoteTemplatePicker onApply={handleApplyTemplate} />}
                    </>
                  )}
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <Button variant="outline" onClick={requestClose} disabled={isSaving}>
                    {isLocked ? "Close" : "Cancel"}
                  </Button>
                  {!isLocked && !isVersionHistoryOpen && (
                    <Button onClick={handleSave} disabled={!canSave}>
                      {saveButtonLabel}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </div>
          </div>
          {isVersionHistoryOpen && note.id ? (
            <NoteVersionHistoryPanel
              noteId={note.id}
              isLocked={isLocked}
              onPreview={handlePreviewVersion}
              onClose={() => setIsVersionHistoryOpen(false)}
              onRestored={handleVersionRestored}
            />
          ) : null}
        </div>
        {discardConfirm}
        <ConfirmDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          title={"Delete note?"}
          description={"This note will be permanently deleted and cannot be recovered."}
          dismiss={deleteNoteMutation.isPending ? "none" : "explicit"}
          cancel={{ onClick: () => setShowDeleteDialog(false), disabled: deleteNoteMutation.isPending }}
          action={{ label: "Delete", onClick: handleDeleteNote, pending: deleteNoteMutation.isPending }}
        />
        <ConfirmDialog
          open={showHideDialog}
          onOpenChange={setShowHideDialog}
          title={note.hidden ? "Unhide note?" : "Hide note?"}
          description={
            note.hidden
              ? "This note will be visible again and restored to AI context."
              : "This note will be hidden from view and removed from AI context. You can unhide it later."
          }
          dismiss={isUpdatingNote ? "none" : "explicit"}
          cancel={{ onClick: () => setShowHideDialog(false), disabled: isUpdatingNote }}
          action={{ label: note.hidden ? "Unhide" : "Hide", onClick: handleToggleHidden, pending: isUpdatingNote }}
        />
      </div>
    </CustomDialogContent>
  )
}
